import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { openMailto } from "@/lib/mailto";

const SUBJECTS = {
  audit: "Free RCM Audit Request",
  consultation: "Consultation Booking Request",
  contact: "Website Contact Message",
};

export default function LeadForm({ type = "audit", compact = false, testIdPrefix = "lead-form" }) {
  const [form, setForm] = useState({
    name: "",
    practice_name: "",
    email: "",
    phone: "",
    looking_for: "",
    monthly_collections: "",
    message: "",
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    openMailto({
      subject: SUBJECTS[type] || "Website Enquiry",
      lines: [
        `Name: ${form.name}`,
        `Practice: ${form.practice_name}`,
        `Email: ${form.email}`,
        form.phone && `Phone: ${form.phone}`,
        form.looking_for && `Looking for: ${form.looking_for}`,
        form.monthly_collections && `Monthly insurance collections: ${form.monthly_collections}`,
        form.message && `\nMessage:\n${form.message}`,
      ],
    });
    toast.success("Opening your email app — just hit send and we'll reply within one business day.");
    setForm({ name: "", practice_name: "", email: "", phone: "", looking_for: "", monthly_collections: "", message: "" });
  };

  const buttonLabel =
    type === "audit" ? "Claim My Free RCM Audit" : type === "consultation" ? "Book My Consultation" : "Send Message";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={testIdPrefix}>
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor={`${testIdPrefix}-name`}>Full Name *</Label>
          <Input id={`${testIdPrefix}-name`} required value={form.name} onChange={set("name")} placeholder="Dr. Jane Smith" className="mt-1.5 focus-visible:ring-teal-500" data-testid={`${testIdPrefix}-name-input`} />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}-practice`}>Practice Name *</Label>
          <Input id={`${testIdPrefix}-practice`} required value={form.practice_name} onChange={set("practice_name")} placeholder="Smile Dental Studio" className="mt-1.5 focus-visible:ring-teal-500" data-testid={`${testIdPrefix}-practice-input`} />
        </div>
      </div>
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor={`${testIdPrefix}-email`}>Work Email *</Label>
          <Input id={`${testIdPrefix}-email`} type="email" required value={form.email} onChange={set("email")} placeholder="you@practice.com" className="mt-1.5 focus-visible:ring-teal-500" data-testid={`${testIdPrefix}-email-input`} />
        </div>
        <div>
          <Label htmlFor={`${testIdPrefix}-phone`}>Phone (optional)</Label>
          <Input id={`${testIdPrefix}-phone`} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="mt-1.5 focus-visible:ring-teal-500" data-testid={`${testIdPrefix}-phone-input`} />
        </div>
      </div>
      {!compact && (
        <>
          <div>
            <Label>What are you looking for?</Label>
            <Select value={form.looking_for} onValueChange={(v) => setForm({ ...form, looking_for: v })}>
              <SelectTrigger className="mt-1.5 focus:ring-teal-500" data-testid={`${testIdPrefix}-looking-for-select`}>
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full-rcm">Full RCM</SelectItem>
                <SelectItem value="ar-recovery">AR Recovery</SelectItem>
                <SelectItem value="denial-management">Denial Management</SelectItem>
                <SelectItem value="insurance-verification">Insurance Verification</SelectItem>
                <SelectItem value="billing-audit">Billing Audit</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Monthly Insurance Collections (optional)</Label>
            <Select value={form.monthly_collections} onValueChange={(v) => setForm({ ...form, monthly_collections: v })}>
              <SelectTrigger className="mt-1.5 focus:ring-teal-500" data-testid={`${testIdPrefix}-collections-select`}>
                <SelectValue placeholder="Select a range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="under-50k">Under $50K</SelectItem>
                <SelectItem value="50k-100k">$50K – $100K</SelectItem>
                <SelectItem value="100k-150k">$100K – $150K</SelectItem>
                <SelectItem value="over-150k">Over $150K</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor={`${testIdPrefix}-message`}>Anything we should know? (optional)</Label>
            <Textarea id={`${testIdPrefix}-message`} value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your current billing setup, denial rate, or AR backlog..." className="mt-1.5 focus-visible:ring-teal-500" data-testid={`${testIdPrefix}-message-input`} />
          </div>
        </>
      )}
      <button
        type="submit"
        data-testid={`${testIdPrefix}-submit`}
        className="w-full rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
      >
        {buttonLabel}
      </button>
      <p className="text-xs text-slate-500 text-center">No obligation. No commitment. Just insights that add value.</p>
    </form>
  );
}
